import React, { useState } from 'react';
import { PostRequest } from 'components/common/index.js';

// Shown when a signed-in but unconfirmed user must confirm their email (the demo
// download gate and the STOP_UNCONFIRMED_USERS_FROM_BROWSING gate). Issue #5238
// replaced the emailed link with a 6-digit code, so the user types the code we
// emailed here; on the correct code onConfirmed() lets the caller continue.
//
// "Wrong email? Change it" flips to an inline email field: a typo'd signup email
// means the code went to the wrong inbox, and under the browsing gate there is no
// other page they could reach to fix it, so the correction has to live here.
//
// Styles are scoped under .confirm-email-code and shipped inline so they travel
// with the JS bundle rather than depending on the stylesheet cascade.
const STYLES = `
  .confirm-email-code { max-width: 360px; margin: 0 auto; text-align: center; padding: 4px 0 8px; }
  .confirm-email-code h4 { font-size: 22px; font-weight: 600; color: #222; margin: 0 0 14px; }
  .confirm-email-code .cec-copy { color: #555; font-size: 15px; line-height: 1.5; margin: 0 0 4px; }
  .confirm-email-code .cec-email { font-weight: 600; color: #222; margin: 0; }
  .confirm-email-code form { display: flex; gap: 10px; justify-content: center; margin: 24px 0 12px; }
  .confirm-email-code form.cec-email-form { display: block; margin: 22px 0 10px; }
  .confirm-email-code .cec-input {
    height: 48px; width: 190px; box-sizing: border-box;
    text-align: center; font-size: 24px; letter-spacing: 8px; text-indent: 8px; font-weight: 600;
    color: #222; border: 1px solid #d5d5d5; border-radius: 8px;
    transition: border-color .15s, box-shadow .15s;
  }
  .confirm-email-code .cec-input::placeholder { color: #cfcfcf; font-weight: 400; }
  .confirm-email-code .cec-email-input {
    height: 46px; width: 100%; box-sizing: border-box; padding: 0 14px;
    font-size: 15px; color: #222; border: 1px solid #d5d5d5; border-radius: 8px;
    transition: border-color .15s, box-shadow .15s;
  }
  .confirm-email-code .cec-email-input::placeholder { color: #cfcfcf; }
  .confirm-email-code .cec-input:focus,
  .confirm-email-code .cec-email-input:focus { outline: none; border-color: #eb5424; box-shadow: 0 0 0 3px rgba(235, 84, 36, .15); }
  .confirm-email-code .cec-verify {
    height: 48px; padding: 0 24px; border: none; border-radius: 8px;
    background: #eb5424; color: #fff; font-size: 16px; font-weight: 600; cursor: pointer;
    transition: background .15s;
  }
  .confirm-email-code form.cec-email-form .cec-verify { width: 100%; height: 46px; margin-top: 12px; }
  .confirm-email-code .cec-verify:hover { background: #cf3f12; }
  .confirm-email-code .cec-verify:disabled { opacity: .6; cursor: default; }
  .confirm-email-code .cec-resend {
    background: none; border: none; color: #eb5424; font-size: 14px; font-weight: 600;
    cursor: pointer; padding: 4px; text-decoration: underline;
  }
  .confirm-email-code .cec-resend:hover { color: #cf3f12; }
  .confirm-email-code .cec-link { display: inline-block; margin-top: 8px; }
  .confirm-email-code .cec-alert { border-radius: 8px; padding: 10px 14px; font-size: 14px; margin: 0 0 12px; text-align: left; }
  .confirm-email-code .cec-alert-error { background: #fdecea; color: #b3261e; }
  .confirm-email-code .cec-alert-ok { background: #e7f4ea; color: #1e7e4f; }
`;

const ConfirmEmailWithCode = ({ onConfirmed, email }) => {
  const [currentEmail, setCurrentEmail] = useState(email || window.currentUserEmail);
  const [mode, setMode] = useState('code');
  const [code, setCode] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const clearMessages = () => { setError(''); setNotice(''); };

  const verifyCode = (e) => {
    e.preventDefault();
    if (submitting) {
      return;
    }

    clearMessages();
    setSubmitting(true);

    PostRequest({
      url: '/verify-confirmation-code',
      data: { code: code },
      callback: (data) => {
        setSubmitting(false);
        if (data.status === 'ok' || data.status === 'already_confirmed') {
          onConfirmed();
        } else {
          setError(data.message);
        }
      }
    }).catch(() => { setSubmitting(false); });
  };

  const resendCode = (e) => {
    e.preventDefault();
    clearMessages();

    PostRequest({
      url: '/resend_confirmation',
      data: {},
      callback: () => { setNotice(t('confirmation_code.resent')); }
    });
  };

  const changeEmail = (e) => {
    e.preventDefault();
    if (submitting || !newEmail) {
      return;
    }

    clearMessages();
    setSubmitting(true);

    PostRequest({
      url: '/update-unconfirmed-email',
      data: { email: newEmail },
      callback: (data) => {
        setSubmitting(false);
        if (data.status === 'ok') {
          setCurrentEmail(data.email);
          setNewEmail('');
          setMode('code');
          setNotice(t('confirmation_code.email_changed'));
        } else {
          setError(data.message);
        }
      }
    }).catch(() => { setSubmitting(false); });
  };

  const goToEmailMode = (e) => { e.preventDefault(); clearMessages(); setMode('email'); };
  const goToCodeMode = (e) => { e.preventDefault(); clearMessages(); setMode('code'); };

  return (
    <div className="confirm-email-code">
      <style>{STYLES}</style>

      <h4> {t('nav.confirmation')}</h4>

      {error && (
        <div className="cec-alert cec-alert-error">
          <i className="fa fa-exclamation-triangle"></i> <strong>{error}</strong>
        </div>
      )}
      {notice && (
        <div className="cec-alert cec-alert-ok">
          <i className="fa fa-check-circle"></i> <strong>{notice}</strong>
        </div>
      )}

      {mode === 'code' && (
        <React.Fragment>
          <p className="cec-copy">{t('sign_up.signed_up_but_unconfirmed')}</p>
          <p className="cec-email">{currentEmail}</p>

          <form onSubmit={verifyCode} autoComplete="off">
            <input
              className="cec-input"
              type="text"
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength="6"
              inputMode="numeric"
              placeholder="123456"
            />
            <input className="cec-verify" type="submit" value={t('confirmation_code.submit_code')} disabled={submitting} />
          </form>

          <button className="cec-resend" onClick={resendCode}>
            {t('confirmation_code.resend_code')}
          </button>
          <br />
          <button className="cec-resend cec-link" onClick={goToEmailMode}>
            {t('confirmation_code.change_email_link')}
          </button>
        </React.Fragment>
      )}

      {mode === 'email' && (
        <React.Fragment>
          <p className="cec-copy">{t('confirmation_code.change_email_prompt')}</p>

          <form className="cec-email-form" onSubmit={changeEmail} autoComplete="off">
            <input
              className="cec-email-input"
              type="email"
              name="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder={t('confirmation_code.change_email_placeholder')}
            />
            <input className="cec-verify" type="submit" value={t('confirmation_code.change_email_submit')} disabled={submitting} />
          </form>

          <button className="cec-resend cec-link" onClick={goToCodeMode}>
            {t('confirmation_code.change_email_back')}
          </button>
        </React.Fragment>
      )}
    </div>
  );
};

export default ConfirmEmailWithCode;
